// Deep import on purpose: the shared prefix lives in a browser-safe leaf module — the
// @civilgrid/utils root entry is Node-only (initializes caches at load) and must never
// reach the client bundle.
import { CUSTOM_DATA_LAYER_ID_PREFIX } from '@civilgrid/utils/src/extract/constants';
import type { ExpressionSpecification } from 'mapbox-gl';
import type { ProjectCustomData } from '~/api/projectCustomData';
import type { TOCItem } from '~/common/types';
import { categoryDisplayName, colorForCategory, humanizeCateTitle } from './customDataSymbology';

// Stable, collision-proof Mapbox layer/source id for a custom-data row (`customData:<id>`) —
// the same string the server contract uses, re-exported under the client's historical name.
export const CUSTOM_DATA_LAYER_PREFIX = CUSTOM_DATA_LAYER_ID_PREFIX;

export function customDataLayerId(rowId: string): string {
  return `${CUSTOM_DATA_LAYER_PREFIX}${rowId}`;
}

// Default raster maxzoom when a row carries none (WMTS rows usually do).
export const DEFAULT_CUSTOM_DATA_MAX_ZOOM = 20;

// Source maxzoom = the deepest zoom tiles are FETCHED at; Mapbox overzooms (scales) that tile
// for higher zooms. Capped at 18 (matching FiberLit) so we don't generate a fresh ST_AsMVT per
// zoom level all the way to 22 — sparse linework gains no detail past this depth.
export const CUSTOM_DATA_VECTOR_MAX_ZOOM = 18;

// WMTS rows that are renderable on the map (ready + raster type).
export function renderableWmtsRows(rows: readonly ProjectCustomData[] | undefined): ProjectCustomData[] {
  return (rows ?? []).filter((r) => r.type === 'wmts' && r.state === 'ready');
}

// Shapefile rows that are renderable on the map (ready + vector type).
export function renderableShapefileRows(rows: readonly ProjectCustomData[] | undefined): ProjectCustomData[] {
  return (rows ?? []).filter((r) => r.type === 'shapefile' && r.state === 'ready');
}

// Build the proxy raster tile URL. The token is appended here (caller passes the
// resolved token) so the upstream WMTS token never reaches the client. Pure: no
// window/axios access, so it is unit-testable under `bun test`.
export function buildProjectRasterTilesUrl(args: {
  tileServerBase: string;
  projectId: string;
  rowId: string;
  token: string;
}): string {
  const { tileServerBase, projectId, rowId, token } = args;
  return `${tileServerBase}/v1/tiles/projectRasters/${rowId}/{z}/{x}/{y}.png?projectId=${projectId}&token=${token}`;
}

// Build the proxy vector tile URL for an ingested shapefile row. Mirrors the raster
// helper; the {z}/{x}/{y} placeholders stay literal so Mapbox fills them per tile.
export function buildProjectVectorTilesUrl(args: {
  tileServerBase: string;
  projectId: string;
  rowId: string;
  token: string;
}): string {
  const { tileServerBase, projectId, rowId, token } = args;
  return `${tileServerBase}/v1/tiles/projectVectors/${rowId}/{z}/{x}/{y}.mvt?projectId=${projectId}&token=${token}`;
}

// Separator between a row's layer id and one of its CateTitle values in a category leaf id.
export const CUSTOM_DATA_CATEGORY_SEGMENT = ':cat:';

// Leaf id for one CateTitle of a row. The value is percent-encoded so it can never contain
// the separator (encodeURIComponent escapes ':'), making the id structure unambiguous for
// any raw value — empty, spaced, unicode, or already containing ':cat:'.
export function customDataCategoryLeafId(rowId: string, value: string): string {
  return `${customDataLayerId(rowId)}${CUSTOM_DATA_CATEGORY_SEGMENT}${encodeURIComponent(value)}`;
}

// Exact inverse of customDataCategoryLeafId; null for anything else (plain row ids, other
// layers' ids, malformed encodings). lastIndexOf is safe because the encoded value holds no ':'.
export function parseCustomDataCategoryLeafId(id: string): { rowId: string; value: string } | null {
  if (!id.startsWith(CUSTOM_DATA_LAYER_PREFIX)) return null;
  const separatorAt = id.lastIndexOf(CUSTOM_DATA_CATEGORY_SEGMENT);
  if (separatorAt <= CUSTOM_DATA_LAYER_PREFIX.length) return null;
  const rowId = id.slice(CUSTOM_DATA_LAYER_PREFIX.length, separatorAt);
  const encoded = id.slice(separatorAt + CUSTOM_DATA_CATEGORY_SEGMENT.length);
  try {
    return { rowId, value: decodeURIComponent(encoded) };
  } catch {
    return null;
  }
}

function categoryLabelKey(layerId: string, value: string): string {
  return `${layerId}::${value}`;
}

/**
 * Display names for every custom-data category in the project, keyed by the row's Mapbox layer id
 * and raw `CateTitle`.
 *
 * The metadata sidebar sees only a bare `CateTitle`, so this is how a name
 * an admin set in the edit dialog reaches it. Categories with no stored name map to the
 * humanized value, so a lookup never has to fall back on its own.
 */
export function buildCustomDataCategoryLabelMap(rows: readonly ProjectCustomData[] | undefined): Map<string, string> {
  const labels = new Map<string, string>();
  for (const row of rows ?? []) {
    for (const category of row.categories) {
      labels.set(categoryLabelKey(customDataLayerId(row.id), category.value), categoryDisplayName(category));
    }
  }
  return labels;
}

// A category's display name for a site that only has the raw `CateTitle`; humanizes it when the
// project's rows aren't loaded yet or the value predates them.
export function customDataCategoryLabel(
  labels: Map<string, string> | undefined,
  layerId: string,
  value: string,
): string {
  return labels?.get(categoryLabelKey(layerId, value)) ?? humanizeCateTitle(value);
}

// A row gets per-category TOC toggles only when there is something to toggle between:
// ready shapefiles with 2+ CateTitle values. WMTS and single-category rows stay flat leaves.
export function isCustomDataCategorySplit(row: ProjectCustomData): boolean {
  return row.type === 'shapefile' && row.state === 'ready' && row.categories.length >= 2;
}

// TOC model for renderable custom data: same WMTS-then-shapefile order as the flat version,
// but multi-category shapefiles become a collapsed parent whose children are per-CateTitle
// leaves (category order = row.categories, already count-desc from ingest).
export function customDataToTocItems(rows: readonly ProjectCustomData[] | undefined): TOCItem[] {
  const toItem = (r: ProjectCustomData): TOCItem =>
    isCustomDataCategorySplit(r)
      ? {
          label: r.label,
          sectionId: customDataLayerId(r.id),
          expanded: false,
          children: r.categories.map((c) => ({
            id: customDataCategoryLeafId(r.id, c.value),
            label: categoryDisplayName(c),
          })),
        }
      : { id: customDataLayerId(r.id), label: r.label };
  return [...renderableWmtsRows(rows).map(toItem), ...renderableShapefileRows(rows).map(toItem)];
}

// Every leaf id customDataToTocItems produces, in tree order. Derived from the tree itself so
// the two can't drift; callers use it to seed default-ON visibility.
export function collectCustomDataLeafIds(rows: readonly ProjectCustomData[] | undefined): string[] {
  const leafIds = (item: TOCItem): string[] => ('children' in item ? item.children.flatMap(leafIds) : [item.id]);
  return customDataToTocItems(rows).flatMap(leafIds);
}

// The row-level id means "this whole row is on, every category drawn" — the only granularity
// the export picker emits, since FME cannot export a subset of a row's categories.
export function isCustomDataRowFullyVisible(row: ProjectCustomData, visibleLayerIds: ReadonlySet<string>): boolean {
  return visibleLayerIds.has(customDataLayerId(row.id));
}

// Whether a row renders at all: the row-level id turns any row fully on, and a split row also
// stays on the map while ANY of its category leaves is toggled ON.
export function isCustomDataRowVisible(row: ProjectCustomData, visibleLayerIds: ReadonlySet<string>): boolean {
  if (isCustomDataRowFullyVisible(row, visibleLayerIds)) return true;
  if (!isCustomDataCategorySplit(row)) return false;
  return row.categories.some((c) => visibleLayerIds.has(customDataCategoryLeafId(row.id, c.value)));
}

// RAW CateTitle values of a split row's toggled-OFF categories, in row order. Derived from
// row.categories (never by decoding leaf ids) so the values stay byte-identical to the tiles.
export function customDataHiddenCategoryValues(row: ProjectCustomData, visibleLayerIds: ReadonlySet<string>): string[] {
  if (!isCustomDataCategorySplit(row) || isCustomDataRowFullyVisible(row, visibleLayerIds)) return [];
  return row.categories
    .filter((c) => !visibleLayerIds.has(customDataCategoryLeafId(row.id, c.value)))
    .map((c) => c.value);
}

// Mapbox filter excluding the row's hidden categories; null when nothing is hidden so the
// existing geometry-type filters stay untouched in the common all-on case.
export function customDataCategoryFilter(
  row: ProjectCustomData,
  visibleLayerIds: ReadonlySet<string>,
): ExpressionSpecification | null {
  const hiddenValues = customDataHiddenCategoryValues(row, visibleLayerIds);
  if (hiddenValues.length === 0) return null;
  // `coalesce` is load-bearing: a null CateTitle arrives with the property ABSENT (SQL `->>`
  // yields NULL and ST_AsMVT drops null properties), while ingest tallies it under ''. Without
  // it, hiding "Uncategorized" would leave those features on the map.
  return ['!', ['in', ['coalesce', ['get', 'CateTitle'], ''], ['literal', hiddenValues]]];
}

// One CateTitle category row in a legend section: color is sourced from the SAME
// `colorForCategory` the map paint uses, so legend swatches can never drift from lines.
export interface CustomDataLegendItem {
  value: string;
  label: string;
  color: string;
  geometryType?: 'point' | 'line' | 'polygon';
}

// A legend group for one visible shapefile layer: its friendly label + per-CateTitle items.
export interface CustomDataLegendSection {
  layerId: string;
  label: string;
  items: CustomDataLegendItem[];
}

// Pure legend model for the visible shapefile vector layers. Each renderable shapefile row that
// is on the map (row id ON, or ANY category leaf ON for split rows) emits a section of CateTitle
// items (color + humanized label from the shared symbology). Rows with no items produce none.
export function buildCustomDataLegendSections(
  rows: readonly ProjectCustomData[] | undefined,
  visibleLayerIds: ReadonlySet<string>,
): CustomDataLegendSection[] {
  const sections: CustomDataLegendSection[] = [];
  for (const row of renderableShapefileRows(rows)) {
    if (!isCustomDataRowVisible(row, visibleLayerIds)) continue;
    // Split rows are filtered per category on the map, so the legend lists only the categories
    // still drawn; a row that is on at the row level draws in full, so all of its categories stay listed.
    const categories =
      isCustomDataCategorySplit(row) && !isCustomDataRowFullyVisible(row, visibleLayerIds)
        ? row.categories.filter((c) => visibleLayerIds.has(customDataCategoryLeafId(row.id, c.value)))
        : row.categories;
    if (categories.length === 0) continue;
    sections.push({
      layerId: customDataLayerId(row.id),
      label: row.label,
      items: categories.map((category) => ({
        value: category.value,
        label: categoryDisplayName(category),
        color: colorForCategory(category),
        geometryType: category.geometryType,
      })),
    });
  }
  return sections;
}
